/**
 * Dock tab renderers.
 * - `header`: a single-panel group's header, as on the reference Outliner and
 *   Inspector ("OUTLINER · 12", "INSPECTOR · 1 selected").
 * - `dock`: an icon + title tab for the bottom dock.
 * - BottomDockActions: the "Console · N warnings" readout on the bottom dock.
 */
import type { IDockviewHeaderActionsProps, IDockviewPanelHeaderProps } from "dockview-react";
import { useLog, useSelection, useWorldSnapshot } from "../hooks/editorHooks";
import { FolderIcon, StatsIcon, TerminalIcon } from "../ui/icons";
import type { IconComponent } from "../ui/icons";

export function PanelHeaderTab({ api }: IDockviewPanelHeaderProps) {
  return (
    <div className="kn-panel-header">
      <span className="kn-panel-header__title">{api.title}</span>
      <span className="kn-panel-header__spacer" />
      <span className="kn-panel-header__meta">
        {api.id === "outliner" ? <EntityCount /> : <SelectionCount />}
      </span>
    </div>
  );
}

function EntityCount() {
  return <>{useWorldSnapshot().entities.size}</>;
}

function SelectionCount() {
  const { ids } = useSelection();
  return <>{ids.length > 0 ? `${ids.length} selected` : ""}</>;
}

const DOCK_ICONS: Record<string, IconComponent> = {
  contentBrowser: FolderIcon,
  console: TerminalIcon,
  statistics: StatsIcon,
};

export function DockTab({ api }: IDockviewPanelHeaderProps) {
  const Icon = DOCK_ICONS[api.id] ?? FolderIcon;
  return (
    <div className="kn-dock-tab">
      <Icon size={13} />
      <span>{api.title}</span>
    </div>
  );
}

/** Shown only on the group that holds the bottom dock's panels. */
export function BottomDockActions({ group }: IDockviewHeaderActionsProps) {
  const isBottomDock = group.panels.some((panel) => panel.id === "console");
  return isBottomDock ? <WarningCount /> : null;
}

function WarningCount() {
  const { counts } = useLog();
  const warnings = counts.Warning + counts.Error;
  return (
    <span className="kn-dock-actions">
      Console · {warnings} {warnings === 1 ? "warning" : "warnings"}
    </span>
  );
}
